import React, { useContext } from 'react';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../contexts/ThemeContext';
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import LightModeLogo from '../assets/images/LT_FK.jpg';
import DarkModeLogo from '../assets/images/LT_FH.jpg';

import LightModeMainLogo from '../assets/images/lightlogo.jpeg';
import DarkModeMainLogo from '../assets/images/darklogo.jpeg';

import SocialMediaSidebar from '../components/SocialMediaSidebar';
import Footer from '../components/Footer';

// Betűországmeséi logó: a public/ mappában lévő logo.jpeg
const betuorszagmeseiLogo = `${process.env.PUBLIC_URL}/logo.jpeg`;
const BETUORSZAGMESEI_URL = 'https://betuorszagmesei.csillagtanoda.hu/';

// Segédkomponens a 3D-s interaktív elemekhez alapzattal
const Interactive3DBox = ({ side, darkMode, children, onClick }: { side: 'left' | 'right', darkMode: boolean, children: React.ReactNode, onClick?: () => void }) => {
  const theme = useTheme();
  // Csak asztali méreten fordítjuk el őket alapból, mobilon szemből vannak
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // Bal oldali elem 45 fokkal jobbra (befelé) néz, a jobb oldali -45 fokkal balra (befelé)
  const initialRotateY = isMdUp ? (side === 'left' ? 45 : -45) : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        width: '100%',
        maxWidth: '400px',
        // A perspektíva biztosítja a 3D hatást
        perspective: '1200px',
      }}
      onClick={onClick}
    >
      {/* A kártya ami fordul és nő */}
      <Box
        component={motion.div}
        initial={{ rotateY: initialRotateY, scale: 1 }}
        // Hover állapot: szembe fordul és felnagyítódik
        whileHover={{ rotateY: 0, scale: 1.1, zIndex: 10 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        sx={{
          backgroundColor: darkMode ? 'rgba(30,16,80,0.88)' : 'rgba(255,255,255,0.92)',
          border: darkMode ? '1px solid rgba(252,198,50,0.35)' : '1px solid rgba(109,27,119,0.2)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: darkMode ? '0 10px 30px rgba(0,0,0,0.8)' : '0 10px 30px rgba(0,0,0,0.2)',
          p: { xs: 3, md: 4 },
          mb: 4, // Távolság, hogy látszódjon alatta az alapzat
          width: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
        }}
      >
        {children}
      </Box>

      {/* Az alapzat (3D-s árnyék platform) ami felett elhelyezkednek */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '70%',
          height: '40px',
          background: darkMode
            ? 'radial-gradient(ellipse at center, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)'
            : 'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 70%)',
          borderRadius: '50%',
          transform: 'rotateX(75deg)', // Fektetett padló hatás
          zIndex: 1,
          pointerEvents: 'none', // Ne akadályozza az egéreseményeket
        }}
      />
    </Box>
  );
};

const Ujdonsagok = () => {
  const { darkMode } = useContext(ThemeContext)!;
  const theme = useTheme();
  const navigate = useNavigate();

  const currentLogo = darkMode ? LightModeLogo : DarkModeLogo;
  const currentMainLogo = darkMode ? LightModeMainLogo : DarkModeMainLogo;

  const openBetuorszag = () => window.open(BETUORSZAGMESEI_URL, '_blank', 'noopener,noreferrer');

  return (
    <Box sx={{ pt: '64px', backgroundColor: 'transparent', overflow: 'hidden' }}>
      <Navbar />
      <SocialMediaSidebar />

      {/* Középre igazított fejléc: cím + rövid tájékoztató */}
      <Box sx={{ textAlign: 'center', px: { xs: 2, md: 6 }, pt: 8, maxWidth: 800, mx: 'auto' }}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          gutterBottom
          sx={{ color: darkMode ? '#ffffff' : '#222222' }}
        >
          Újdonságok – Részletes Információk
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
        >
          Itt találod a legfrissebb híreket, eseményeket és fejlesztéseket! Folyamatosan azon dolgozunk,
          hogy minél több értékes tartalommal és lehetőséggel bővítsük szolgáltatásainkat.
          Nézz vissza gyakran, hogy ne maradj le semmiről!
        </Typography>
      </Box>

      {/* Fő konténer: 3 oszlopos elrendezés asztali nézeten */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, md: 6 },
          py: { xs: 6, md: 8 },
          gap: { xs: 8, md: 4 },
          minHeight: 'calc(100vh - 280px)',
        }}
      >
        {/* Bal oldal: Betűországmeséi kártya 45 fokban befelé fordítva */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Interactive3DBox side="left" darkMode={darkMode} onClick={openBetuorszag}>
            <Box
              component="img"
              src={betuorszagmeseiLogo}
              alt="Betűországmeséi logó"
              sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                objectFit: 'cover',
                mb: 2,
                display: 'block',
                mx: 'auto',
              }}
            />
            <Typography variant="h5" fontWeight="600" textAlign="center">
              Betűországmeséi
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', mt: 1, color: theme.palette.text.secondary, lineHeight: 1.7 }}
            >
              A Betűországmeséi egy olyan oldal, ahol bárki ingyenesen feliratkozhat, és
              megkaphatja a meséket írásos formában. Iratkozz fel, és a történetek érkeznek hozzád!
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  openBetuorszag();
                }}
                sx={{
                  bgcolor: '#6D1B77',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '20px',
                  '&:hover': { bgcolor: '#C92BB5' },
                }}
              >
                Ingyenes feliratkozás
              </Button>
            </Box>
          </Interactive3DBox>
        </Box>

        {/* Középső rész: Letölthető tartalmak logó (Border és világítás nélkül, fixen áll) */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            component={motion.img}
            src={currentLogo}
            alt="Letölthető tartalmak logó"
            onClick={() => navigate('/letolthetotartalmak')}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            sx={{
              width: { xs: 160, md: 240 },
              height: 'auto',
              cursor: 'pointer',
              background: 'transparent',
            }}
          />
        </Box>

        {/* Jobb oldal: AI kártya -45 fokban befelé fordítva */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Interactive3DBox side="right" darkMode={darkMode} onClick={() => navigate('/ai')}>
            <Box
              component="img"
              src={currentMainLogo}
              alt="Mesterséges intelligencia képzés logó"
              sx={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                objectFit: 'cover',
                mb: 2,
                display: 'block',
                mx: 'auto',
              }}
            />
            <Typography variant="h5" fontWeight="600" textAlign="center">
              Mesterséges intelligencia képzés
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', mt: 1, color: theme.palette.text.secondary }}
            >
              Ismerkedj meg a mesterséges intelligencia alapjaival, és fedezd fel a jövő technológiáit!
            </Typography>
          </Interactive3DBox>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default Ujdonsagok;